import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import { marked } from "marked";

const CTA_URL = "https://www.saffronedge.com/contact-us/";
const RENDER_W = 794; // px width of the off-screen render — maps to full A4 width

export async function downloadPdfFromMarkdown(markdown, pdfFilename) {
  const htmlContent = marked(markdown);

  // ── Build off-screen render container (CONTENT ONLY — no CTA) ──────────────
  // The CTA is rendered and placed separately (see below) so it can never be
  // sliced across a page boundary by the content-pagination algorithm.
  const container = document.createElement("div");
  container.style.cssText =
    "position:fixed;top:-99999px;left:-99999px;z-index:-1;";

  const target = document.createElement("div");
  target.id = "__pdf_render__";
  target.style.cssText =
    `width:${RENDER_W}px;padding:48px;font-family:'Segoe UI',Arial,sans-serif;` +
    "font-size:13px;line-height:1.7;color:#111;background:#fff;box-sizing:border-box;";

  target.innerHTML = `
    <img src="/saffronos.png"
         style="width:110px;margin-bottom:22px;"
         onerror="this.style.display='none'"/>
    ${htmlContent}
  `;

  const style = document.createElement("style");
  style.textContent = `
    #__pdf_render__ h1 { font-size:22px; font-weight:700; margin:24px 0 10px; color:#0f172a; }
    #__pdf_render__ h2 { font-size:18px; font-weight:600; margin:20px 0 8px; color:#1e293b;
                         border-bottom:1px solid #e2e8f0; padding-bottom:4px; }
    #__pdf_render__ h3 { font-size:15px; font-weight:600; margin:16px 0 6px; color:#1e293b; }
    #__pdf_render__ h4 { font-size:13px; font-weight:600; margin:12px 0 4px; }
    #__pdf_render__ p  { margin:0 0 10px; }
    #__pdf_render__ table { border-collapse:collapse; width:100%; margin:14px 0; font-size:12px; }
    #__pdf_render__ th,
    #__pdf_render__ td  { border:1px solid #cbd5e1; padding:7px 10px; text-align:left; vertical-align:top; }
    #__pdf_render__ th  { background:#f1f5f9; font-weight:600; color:#334155; }
    #__pdf_render__ tr:nth-child(even) td { background:#f8fafc; }
    #__pdf_render__ ul,
    #__pdf_render__ ol  { padding-left:22px; margin:6px 0 10px; }
    #__pdf_render__ li  { margin:3px 0; }
    #__pdf_render__ pre { background:#f8fafc; border:1px solid #e2e8f0; padding:12px;
                          border-radius:4px; font-size:11px; white-space:pre-wrap;
                          word-break:break-all; margin:10px 0; }
    /* Inline highlights (code / mark) must stay on ONE line. html2canvas paints
       an inline element's background from its union getBoundingClientRect(), so
       a highlighted span that WRAPS across lines fills one large rectangle that
       covers — and hides — the surrounding non-highlighted text. white-space:nowrap
       makes the span wrap as a whole unit to the next line instead, so its
       background only ever covers its own single-line box. */
    #__pdf_render__ code { background:#f1f5f9; color:#0f172a; padding:1px 5px; border-radius:3px;
                          font-size:11px; white-space:nowrap; }
    #__pdf_render__ mark { background:#fef08a; color:#111827; padding:0 3px; border-radius:2px;
                          white-space:nowrap; }
    #__pdf_render__ blockquote { border-left:3px solid #94a3b8; margin:10px 0;
                                 padding:4px 0 4px 14px; color:#475569; }
    #__pdf_render__ hr { border:none; border-top:1px solid #e2e8f0; margin:18px 0; }
    #__pdf_render__ strong { font-weight:600; }
    #__pdf_render__ a { color:#0891b2; text-decoration:none; }
    .__pre_line__ { display:block; min-height:1em; }
  `;

  container.appendChild(target);
  container.appendChild(style);
  document.body.appendChild(container);

  // ── Split <pre> blocks into per-line spans so breaks land between lines ───
  target.querySelectorAll("pre").forEach((pre) => {
    const text = pre.textContent;
    const lines = text.split("\n");
    if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();

    const frag = document.createDocumentFragment();
    lines.forEach((line) => {
      const span = document.createElement("span");
      span.className = "__pre_line__";
      span.textContent = line || " ";
      frag.appendChild(span);
    });

    pre.textContent = "";
    pre.appendChild(frag);
  });

  // Track every off-screen node we append so cleanup on error is reliable.
  let ctaContainer = null;

  // Helper: wait for all <img> in a node to finish loading.
  const waitForImages = async (node) => {
    const imgs = Array.from(node.querySelectorAll("img"));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }),
      ),
    );
  };

  try {
    // ── Page geometry ─────────────────────────────────────────────────────────
    const pdf = new jsPDF("p", "mm", "a4");
    const PAGE_W_MM = pdf.internal.pageSize.getWidth(); // 210 mm
    const PAGE_H_MM = pdf.internal.pageSize.getHeight(); // 297 mm

    const PX_PER_MM = RENDER_W / PAGE_W_MM;
    const PAGE_H_CSS = PAGE_H_MM * PX_PER_MM; // ≈ 1123 px

    const PAGE_PAD_MM = 10;
    const PAGE_PAD_CSS = PAGE_PAD_MM * PX_PER_MM; // ≈ 27 px

    const USABLE_H_CSS = PAGE_H_CSS - PAGE_PAD_CSS * 2;
    const CSS_TO_MM = PAGE_W_MM / RENDER_W;

    // ── Wait for assets ───────────────────────────────────────────────────────
    await waitForImages(target);
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch {
        /* best-effort */
      }
    }
    void target.offsetHeight;

    // ── Measure positions ─────────────────────────────────────────────────────
    const targetRect = target.getBoundingClientRect();
    const CONTENT_H_CSS = targetRect.height;

    const atoms = Array.from(
      target.querySelectorAll(
        "h1,h2,h3,h4,h5,h6,p,li,blockquote,tr,img,.__pre_line__",
      ),
    )
      .map((el) => {
        const rect = el.getBoundingClientRect();
        return {
          top: rect.top - targetRect.top,
          bottom: rect.bottom - targetRect.top,
        };
      })
      .sort((a, b) => a.top - b.top || a.bottom - b.bottom);

    // ── Greedy pagination ─────────────────────────────────────────────────────
    const breaks = [0];
    let pageStart = 0;

    for (const a of atoms) {
      if (a.bottom <= pageStart) continue;

      if (a.bottom - pageStart > USABLE_H_CSS) {
        if (a.top > pageStart) {
          breaks.push(a.top);
          pageStart = a.top;
        }
        while (a.bottom - pageStart > USABLE_H_CSS) {
          pageStart += USABLE_H_CSS;
          breaks.push(pageStart);
        }
      }
    }

    // ── Render content to canvas ──────────────────────────────────────────────
    const DPR = 1.5;
    const canvas = await html2canvas(target, {
      scale: DPR,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });
    document.body.removeChild(container);

    const MM_PER_CANVAS_PX = PAGE_W_MM / canvas.width;
    const CSS_TO_CANVAS = canvas.height / CONTENT_H_CSS;
    const usableHeightPx = Math.round(USABLE_H_CSS * CSS_TO_CANVAS);

    // Track the bottom edge (mm) of the content on the LAST page, so the CTA can
    // be appended directly beneath it if there is room.
    let lastPageBottomMm = 0;

    // ── Slice content canvas into pages ───────────────────────────────────────
    for (let i = 0; i < breaks.length; i++) {
      if (i > 0) pdf.addPage();

      const sliceTopCss = breaks[i];
      const sliceEndCss = i + 1 < breaks.length ? breaks[i + 1] : CONTENT_H_CSS;

      const sliceTopPx = Math.round(sliceTopCss * CSS_TO_CANVAS);
      const sliceHeightPx = Math.round(
        (sliceEndCss - sliceTopCss) * CSS_TO_CANVAS,
      );
      const clampedHeight = Math.min(
        sliceHeightPx,
        canvas.height - sliceTopPx,
        usableHeightPx,
      );

      if (clampedHeight <= 0) continue;

      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = clampedHeight;
      const ctx = pageCanvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(
        canvas,
        0,
        sliceTopPx,
        canvas.width,
        clampedHeight,
        0,
        0,
        canvas.width,
        clampedHeight,
      );

      const sliceHeightMm = clampedHeight * MM_PER_CANVAS_PX;
      const yOff = i > 0 ? PAGE_PAD_MM : 0;
      const drawnHeightMm = Math.min(sliceHeightMm, PAGE_H_MM - yOff);

      pdf.addImage(
        pageCanvas.toDataURL("image/jpeg", 0.85),
        "JPEG",
        0,
        yOff,
        PAGE_W_MM,
        drawnHeightMm,
      );

      lastPageBottomMm = yOff + drawnHeightMm;
    }

    // ── Render the CTA as its own self-contained image ────────────────────────
    // Rendering it separately guarantees it is placed as one indivisible unit:
    // appended below the last page's content if it fits, otherwise on a fresh
    // page. Either way it is NEVER sliced across a page boundary.
    ctaContainer = document.createElement("div");
    ctaContainer.style.cssText =
      "position:fixed;top:-99999px;left:-99999px;z-index:-1;";

    const ctaWrap = document.createElement("div");
    // Match the content's horizontal padding (48px) so the CTA aligns with the
    // body text. No vertical padding — vertical spacing is added in mm below.
    ctaWrap.style.cssText =
      `width:${RENDER_W}px;padding:0 48px;box-sizing:border-box;background:#fff;` +
      "font-family:'Segoe UI',Arial,sans-serif;";
    // Subtle single-line strip — matches the grayscale report: no colour fills,
    // just a thin top rule, small logo left, text centre, small button right.
    ctaWrap.innerHTML = `
      <div style="display:flex;align-items:center;gap:18px;border-top:1px solid #e2e8f0;padding-top:16px;">
        <img src="/logo.webp"
             style="width:70px;flex-shrink:0;"
             onerror="this.style.display='none'"/>
        <div style="flex:1;font-size:11px;line-height:1.5;color:#475569;">
          <span style="font-weight:700;color:#0f172a;">Need assistance in implementation or have questions?</span>
          Our team of experts is ready to help you implement these recommendations.
        </div>
        <span id="__pdf_cta_btn__"
              style="flex-shrink:0;display:inline-block;padding:9px 18px;background:#0f172a;color:#fff;font-size:11px;font-weight:600;border-radius:6px;white-space:nowrap;">
          Contact Us &#8594;
        </span>
      </div>
    `;
    ctaContainer.appendChild(ctaWrap);
    document.body.appendChild(ctaContainer);

    await waitForImages(ctaWrap);
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch {
        /* best-effort */
      }
    }
    void ctaWrap.offsetHeight;

    // Measure button position relative to the CTA wrap (CSS px).
    const ctaWrapRect = ctaWrap.getBoundingClientRect();
    const ctaBtn = ctaWrap.querySelector("#__pdf_cta_btn__");
    const btnRect = ctaBtn.getBoundingClientRect();
    const btnCss = {
      top: btnRect.top - ctaWrapRect.top,
      left: btnRect.left - ctaWrapRect.left,
      width: btnRect.width,
      height: btnRect.height,
    };

    const ctaCanvas = await html2canvas(ctaWrap, {
      scale: DPR,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });
    document.body.removeChild(ctaContainer);
    ctaContainer = null;

    const ctaHeightMm = (ctaCanvas.height / ctaCanvas.width) * PAGE_W_MM;

    // Decide placement: append under last page's content if it fits, else new page.
    const GAP_MM = 8;
    const bottomLimitMm = PAGE_H_MM - PAGE_PAD_MM;
    let ctaTopMm;
    if (
      lastPageBottomMm > 0 &&
      lastPageBottomMm + GAP_MM + ctaHeightMm <= bottomLimitMm
    ) {
      ctaTopMm = lastPageBottomMm + GAP_MM; // same page, below content
    } else {
      pdf.addPage();
      ctaTopMm = PAGE_PAD_MM; // fresh page
    }

    pdf.addImage(
      ctaCanvas.toDataURL("image/jpeg", 0.92),
      "JPEG",
      0,
      ctaTopMm,
      PAGE_W_MM,
      ctaHeightMm,
    );

    // ── Clickable link annotation over the button ─────────────────────────────
    // The image is just pixels; pdf.link() writes a real PDF URI annotation that
    // PDF viewers treat as a genuine hyperlink. Coordinates are exact because we
    // control precisely where the CTA image was placed.
    const linkX = btnCss.left * CSS_TO_MM;
    const linkY = ctaTopMm + btnCss.top * CSS_TO_MM;
    const linkW = btnCss.width * CSS_TO_MM;
    const linkH = btnCss.height * CSS_TO_MM;
    pdf.link(linkX, linkY, linkW, linkH, { url: CTA_URL });

    pdf.save(pdfFilename);
  } catch (err) {
    if (document.body.contains(container)) document.body.removeChild(container);
    if (ctaContainer && document.body.contains(ctaContainer)) {
      document.body.removeChild(ctaContainer);
    }
    throw err;
  }
}
